import React, { useState, useRef, useEffect } from 'react';
import './PlayerDetails.css';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from "../context/UserContext";
import Swal from 'sweetalert2';
import { hasPrivilege } from '../utils/hasPrivilege';
const PlayerDetails = () => {
  const navigate = useNavigate();
  const [player, setPlayer] = useState(null);
  const [batches, setBatches] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { userData } = useUser();
  const token = localStorage.getItem('auth-token');
  const userId = userData?.userId;
  const [openBatchId, setOpenBatchId] = useState(null);
  const wrapperRef = useRef({});
  const location = useLocation();
  const playerId = location.state?.userId;

  useEffect(() => {
    if (!playerId) return;
    const fetchPlayerData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_GET_PLAYER}/${playerId}/player-info?LoginUser=${userId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          }
        });
        if (!response.ok) throw new Error('Network response was not ok');
        const data = await response.json();
        if (data.success) {
          const p = data.data.player;
          setPlayer({
            id: p.playerId || playerId,
            playerUserId: p.playerUserId,
            name: p.playerName,
            mobile: p.mobileNumber || 'NA',
            gender: p.gender.charAt(0).toUpperCase() + p.gender.slice(1).toLowerCase(),
            dob: p.dob || 'NA',
            email: 'NA',
            category: p.playerCategory || 'NA',
            level: p.playerLevel || 'NA',
            profile: p.playerProfile || 'NA',
            parentNumber: Array.isArray(p.parentDetails) && p.parentDetails.length > 0 ? p.parentDetails[0].mobileNumber : 'NA',
            parentName: Array.isArray(p.parentDetails) && p.parentDetails.length > 0 ? p.parentDetails[0].name : 'NA',
            relation: Array.isArray(p.parentDetails) && p.parentDetails.length > 0 ? p.parentDetails[0].relation : 'NA',
          });

          setBatches(
            p.batches.map(b => ({
              playerId: p.playerId || playerId,
              id: b.batchId,
              batchName: b.batchName,
              academyName: b.academyName,
              name: b.name,
              primary: b.primary,
              status: b.status ? 'No' : 'Yes',
            }))
          );

          setSubscriptions(
            p.playingSubscriptions.map(s => ({
              name: s.playerName,
              phone: s.mobileNumber,
              preferredCenter: s.academyName,
              purchaseDate: s.purchaseDate.split('T')[0],
              joiningDate: s.joiningDate.split('T')[0],
              expiryDate: s.expiryDate.split('T')[0],
              planType: s.planType || 'NA',
              sportType: s.sportName,
              preferredSport: s.sportName,
              totalPlayingHours: s.totalHours,
              hoursUsed: s.totalHours - s.hoursRemaining,
            }))
          );
          setError(null);
        } else {
          setError('Failed to fetch player data');
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPlayerData();
  }, [playerId, userId]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        openBatchId &&
        wrapperRef.current[openBatchId] &&
        !wrapperRef.current[openBatchId].contains(e.target)
      ) {
        setOpenBatchId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openBatchId]);

  const handleActionClick = batchId => {
    setOpenBatchId(prev => (prev === batchId ? null : batchId));
  };

  const handleAttendance = (event, batch) => {
    const url = `/playerAttendance?batchId=${batch.id}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
    setOpenBatchId(null);
  };

  const handlePerformance = (event, batch) => {
    const url = `/performance?batchId=${batch.id}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { batchId: batch.id, playerId: playerId } });
    }
    setOpenBatchId(null);
  };


  const handleEnable = (event, batchId, playerId) => {
    const url = `/addPlayerPayment?batchId=${batchId}&playerId=${playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { batchId, playerId } });
    }
  };


  const handleDelete = async (batchId, playerId) => {
    const url = `${process.env.REACT_APP_API_DELETE_PLAYER}?playerId=${playerId}&batchId=${batchId}`;
    const headers = {
      "Content-Type": "application/json",
      'Authorization': `Bearer ${token}`,
    };

    try {
      const response = await fetch(url, {
        method: "DELETE",
        headers: headers,
      });

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Successfully removed",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
        setBatches(prev =>
          prev.map(b => {
            if (b.id === batchId) {
              return {
                ...b,
                primary: false,
                status: "Yes",
              };
            }
            return b;
          })
        );
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to delete",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setOpenBatchId(null);
    }
  };

  const handleSetPrimary = async (batchId, playerId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MAKE_PRIMARY}?batch_id=${batchId}&player_id=${playerId}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        Swal.fire({
          icon: "success",
          title: "Success!",
          text: "Player has been set as primary.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Error!",
          text: "Failed to set primary player.",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 3000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Something went wrong.",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
    } finally {
      setOpenBatchId(null);
    }
  };

  const handleUpdatePayment = (event, batch) => {
    const url = `/playerpaymentDetail?batchId=${batch.id}&playerId=${batch.playerId || playerId}`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { batchId: batch.id, playerId: batch.playerId || playerId } });
    }
    setOpenBatchId(null);
  };


  const handleLogs = (playerUserId) => {
    navigate('/ActivityLog', { state: { playerUserId } });
  };

  return (
    <div className="enrollment-container" style={{ overflow: "visible" }}>
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && !player && (
        <div className="error-message">No data available</div>
      )}

      {!loading && !error && player && (
        <>
          <div className="search-bar">
            <h2 className="section-title">Player Details</h2>
            {(hasPrivilege(userData, 'ACTIVITY_LOGS', 'ACTIVITY_LOGS_ALL_PLAYER_ACCESS') ||
              hasPrivilege(userData, 'ACTIVITY_LOGS', 'ACTIVITY_LOGS_SINGLE_PLAYER_ACCESS')) && (
                <button className="create-button" onClick={() => handleLogs(player.playerUserId)}>Activity Logs</button>
              )}
          </div>

          <table className="player-table-details">
            <tbody>
              <tr><th>Name</th><td>{player.name}</td></tr>
              <tr><th>Mobile</th><td>{player.mobile}</td></tr>
              <tr><th>Gender</th><td>{player.gender}</td></tr>
              <tr><th>Date of Birth</th><td>{player.dob}</td></tr>
              <tr><th>Player Category</th><td>{player.category}</td></tr>
              <tr><th>Player Level</th><td>{player.level}</td></tr>
              <tr><th>Player Profile</th><td>{player.profile}</td></tr>
              <tr><th>Parent Name</th><td>{player.parentName}</td></tr>
              <tr><th>Parent Number</th><td>{player.parentNumber}</td></tr>
              <tr><th>Relation</th><td>{player.relation}</td></tr>
            </tbody>
          </table>

          {batches && batches.length > 0 && (
            <>
              <h2 className="section-title">Player Batches</h2>
              <table className="player-table-details">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Academy Name</th>
                    <th>Batch Name</th>
                    <th>Primary</th>
                    <th>Removed</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {batches.map(b => {
                    const isPrimary = String(b.primary).toLowerCase() === "true";
                    return (
                      <tr key={b.id}>
                        <td>{b.name}</td>
                        <td>{b.academyName}</td>
                        <td>{b.batchName}</td>
                        <td>{isPrimary ? 'True' : 'False'}</td>
                        <td>{b.status}</td>
                        <td>
                          <div
                            className="action-wrapper"
                            ref={el => (wrapperRef.current[b.id] = el)}
                          >
                            <button
                              className="action-btn-details"
                              onClick={() => handleActionClick(b.id)}
                            >
                              ⋮
                            </button>

                            {openBatchId === b.id && (
                              <ul className="dropdown-menu-details" >
                                <li style={{ padding: '5px' }}>
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => handleAttendance(e, b)}
                                  >
                                    Attendance
                                  </button>
                                </li>
                                <li style={{ padding: '5px' }}>
                                  <button
                                    type="button"
                                    className="dropdown-item"
                                    onClick={(e) => handlePerformance(e, b)}
                                  >
                                    Performance
                                  </button>
                                </li>

                                {userData?.userType === 'ADMIN' && (
                                  <>
                                    <li style={{ padding: '5px' }}>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={(e) => {
                                          b.status === 'Yes'
                                            ? handleEnable(e, b.id, b.playerId)
                                            : handleDelete(b.id, b.playerId)
                                        }}
                                      >
                                        {b.status === 'Yes' ? 'Enable' : 'Remove'}
                                      </button>
                                    </li>


                                    {!isPrimary && (
                                      <li style={{ padding: '5px' }}>
                                        <button
                                          type="button"
                                          className="dropdown-item"
                                          onClick={() => handleSetPrimary(b.id, b.playerId)}
                                        >
                                          Make this batch as Primary
                                        </button>
                                      </li>
                                    )}

                                    <li style={{ padding: '5px' }}>
                                      <button
                                        type="button"
                                        className="dropdown-item"
                                        onClick={(e) => handleUpdatePayment(e, b)}
                                      >
                                        Update Player Payment Details
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {subscriptions && subscriptions.length > 0 && (
            <>
              <h2 className="section-title">Playing Subscription</h2>
              <div className="table-wrapper">
                <table className="player-table-details">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Preferred Center</th>
                      <th>Purchase Date</th>
                      <th>Joining Date</th>
                      <th>Expiry Date</th>
                      <th>Plan Type</th>
                      <th>Sport Type</th>
                      <th>Preferred Sport</th>
                      <th>Total Playing Hours</th>
                      <th>Hours Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subscriptions.map((sub, idx) => (
                      <tr key={idx}>
                        <td>{sub.name}</td>
                        <td>{sub.phone}</td>
                        <td>{sub.preferredCenter}</td>
                        <td>{sub.purchaseDate}</td>
                        <td>{sub.joiningDate}</td>
                        <td>{sub.expiryDate}</td>
                        <td>{sub.planType}</td>
                        <td>{sub.sportType}</td>
                        <td>{sub.preferredSport}</td>
                        <td>{sub.totalPlayingHours}</td>
                        <td>{sub.hoursUsed}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default PlayerDetails;
